import { Capacitor } from '@capacitor/core'
import { IonRouterOutlet } from '@ionic/react'
import { IonReactRouter } from '@ionic/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Redirect, Route } from 'react-router'
import { Bounce, ToastContainer, toast } from 'react-toastify'

import {
  useMyMonitoringManagementGetCompaniesSelectList,
  useMyMonitoringManagementThemeCreateOrUpdate
} from '@apps-common/apis/my-monitoring/management'
import { SuspenseFallbackMyMonitoring } from '@apps-common/components/fallback'
import { ThemeManagementMenu } from '@apps-common/components/theme'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { NotificationProvider, useAuth } from '@apps-common/contexts'
import { ControlProvider } from '@apps-common/contexts/ControlContext'
import { useMyMonitoringPush } from '@apps-common/hooks/useMyMonitoringPush'
import { useTheme } from '@apps-common/hooks/useTheme'
import { useWindowDimensions } from '@apps-common/hooks/useWindowDimensions'
import { AUTH_CLAIMS } from '@apps-common/lib'
import { pageAnimationBuilder } from '@apps-common/lib/page-animations'
import { TOAST_ID_APP, TOAST_ID_PUSH_NOTIFICATION } from '@apps-common/lib/toast'
import { routeRenderer } from '@apps-common/utils/route-renderer'
import { PRIVATE_ROUTES, ROUTE_SITES } from '@apps-my-monitoring/config/private-routes'
import { PUBLIC_ROUTES, ROUTE_AUTHENTICATE } from '@apps-my-monitoring/config/public-routes'

import { UniversalLinkHandler } from '@apps-common/components/UniversalLinkHandler'
import { MY_MONITORING_UNIVERSAL_LINKS } from '@apps-common/config/universal-links'
import 'react-toastify/dist/ReactToastify.min.css'
import { FloatingMenu } from '../components/navigation/floating-menu'
import { SwipeNavigationGesture } from '../components/navigation/swipe-navigation'

const redirectRoutes = { authenticated: ROUTE_SITES, unauthenticated: ROUTE_AUTHENTICATE }

export const AppRoutes = () => {
  const { initialized, user } = useAuth()

  const { isDarkMode } = useTheme()
  const { isMobile } = useWindowDimensions()
  const isNative = Capacitor.isNativePlatform()
  const shouldAutoDismissPushToasts = isNative || isMobile
  const [pushToastCount, setPushToastCount] = useState(0)
  const [pushToastCountImmediate, setPushToastCountImmediate] = useState(0)
  const pushToastIdsRef = useRef<Set<string | number>>(new Set())
  const dismissAllPendingRef = useRef(false)

  const hosts = useMemo(() => MY_MONITORING_UNIVERSAL_LINKS, [])

  useEffect(() => {
    const unsubscribe = toast.onChange((payload: any) => {
      if (!payload || payload?.containerId !== TOAST_ID_PUSH_NOTIFICATION) {
        return
      }

      if (payload.status === 'added' || payload.status === 'updated') {
        pushToastIdsRef.current.add(payload.id)
      } else if (payload.status === 'removed') {
        pushToastIdsRef.current.delete(payload.id)
      }

      const nextCount = pushToastIdsRef.current.size

      setPushToastCount(nextCount)

      if (dismissAllPendingRef.current) {
        if (nextCount === 0) {
          dismissAllPendingRef.current = false
          setPushToastCountImmediate(0)
        }

        return
      }

      setPushToastCountImmediate(nextCount)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  if (!initialized) {
    return <SuspenseFallbackMyMonitoring />
  }

  const buildRedirect = () => {
    const path = user ? ROUTE_SITES.path : ROUTE_AUTHENTICATE.path
    const query = window?.location?.search || ''

    return `${path}${query}`
  }

  return (
    <IonReactRouter>
      <UniversalLinkHandler hosts={hosts} />
      <NotificationProvider pushHook={useMyMonitoringPush}>
        <ThemeManagementMenu
          roles={AUTH_CLAIMS.myMonitoringManagementAdmin.roles}
          scopes={AUTH_CLAIMS.myMonitoringManagementAdmin.scopes}
          companiesData={useMyMonitoringManagementGetCompaniesSelectList}
          createOrUpdateData={useMyMonitoringManagementThemeCreateOrUpdate}
        />
        <FloatingMenu />
        <SwipeNavigationGesture />
        <ControlProvider>
          <IonRouterOutlet id='main' animated animation={pageAnimationBuilder}>
            {PUBLIC_ROUTES.map((r) => (
              <Route key={r.path} path={r.path} render={() => routeRenderer(user, <r.component />, r.type, redirectRoutes)} exact />
            ))}
            {PRIVATE_ROUTES.map((r) => (
              <Route key={r.path} path={r.path} render={() => routeRenderer(user, <r.component />, r.type, redirectRoutes)} exact />
            ))}
            <Route render={() => <Redirect to={buildRedirect()} />} />
          </IonRouterOutlet>
        </ControlProvider>
        <ToastContainer
          stacked
          containerId={TOAST_ID_PUSH_NOTIFICATION}
          position={isMobile ? 'top-center' : 'top-right'}
          autoClose={shouldAutoDismissPushToasts ? 6000 : false}
          hideProgressBar
          closeButton={false}
          newestOnTop={false}
          closeOnClick
          draggable='touch'
          draggableDirection='y'
          pauseOnFocusLoss
          pauseOnHover
          transition={Bounce}
          className={`${isMobile ? 'push-notification-toast--mobile' : ''} ${isNative ? 'push-notification-toast--native' : ''} ${pushToastCount > 1 ? 'push-notification-toast--with-dismiss' : ''}`.trim()}
        />
        <AnimatePresence>
          {pushToastCountImmediate > 1 && (
            <motion.div
              key='react-toastify-push-notifications-dismiss-all'
              {...motionFadeIn(0.25)}
              className='push-notifications-push-toast-dismiss'
            >
              <button
                type='button'
                className='rounded-full bg-brand text-on-brand ring-2 ring-offset-1 ring-brand ring-offset-surface pl-3 pr-2 py-[2px] text-[0.75rem] shadow-[0_8px_22px_hsl(var(--rn-bg)/0.32)] hover:cursor-pointer flex items-center justify-center gap-1'
                onClick={() => {
                  dismissAllPendingRef.current = true
                  pushToastIdsRef.current.clear()
                  setPushToastCountImmediate(0)
                  toast.dismiss({ containerId: TOAST_ID_PUSH_NOTIFICATION })
                }}
              >
                Dismiss all <commonIcons.arrowRight className='text-[1rem]' />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        <ToastContainer
          stacked
          containerId={TOAST_ID_APP}
          position='bottom-center'
          autoClose={4000}
          hideProgressBar
          closeButton={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDarkMode ? 'dark' : 'light'}
        />
      </NotificationProvider>
    </IonReactRouter>
  )
}
